import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FormInput,
  PrimarySecondaryButtonContainer,
  SubmitButton,
} from '@/components/FormComponents';
import { FormErrorAlert } from '@/components/FormErrorAlert';
import { Card } from '@design-system';
import { isValidEmailFormat, normalizePhoneNumber } from '@shared';
import { useAdvisorSearch } from '../hooks/use-advisor-search';
import { useUpdateContactInfo } from '../hooks/use-update-contact-info';
import { getErrorMessage } from '../utils/get-error-message';

const searchSchema = z.object({
  advisorQuery: z.string().min(1, 'Advisor ID or Email is required'),
});

const isValidUsPhoneNumber = (val: string) => /^\+1\d{10}$/.test(normalizePhoneNumber(val));

const emailSchema = z.object({
  advisorEmail: z
    .string()
    .min(1, 'Email is required')
    .refine(isValidEmailFormat, 'Invalid email format'),
});

const phoneSchema = z.object({
  advisorPhoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .refine(isValidUsPhoneNumber, 'Invalid US phone number. Enter a 10-digit number.'),
});

const UPDATABLE_AUTH_TYPES = new Set(['email', 'sms']);

function getAuthTypeErrorMessage(auth0UserId: string | null | undefined): string {
  const prefix = auth0UserId?.split('|')[0];
  if (!prefix) {
    return 'No Auth0 account is linked to this advisor. Account identifiers cannot be updated.';
  }
  if (prefix.startsWith('google')) {
    return 'This advisor uses Google login. Account identifiers cannot be updated for this connection type.';
  }
  if (prefix === 'windowslive') {
    return 'This advisor uses Microsoft login. Account identifiers cannot be updated for this connection type.';
  }
  return `This advisor uses "${prefix}" authentication. Account identifiers cannot be updated for this connection type.`;
}

export function AccountIdentifiersForm() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQueryRef = useRef(searchParams.get('advisorId') ?? '');

  const searchForm = useForm<z.infer<typeof searchSchema>>({
    resolver: zodResolver(searchSchema),
    defaultValues: { advisorQuery: initialQueryRef.current },
  });

  const emailForm = useForm<z.infer<typeof emailSchema>>({
    resolver: zodResolver(emailSchema),
  });

  const phoneForm = useForm<z.infer<typeof phoneSchema>>({
    resolver: zodResolver(phoneSchema),
  });

  const {
    searchAdvisor,
    advisor,
    isPending: isSearching,
    isError: isSearchError,
    error: searchError,
  } = useAdvisorSearch();

  const {
    updateContactInfo: updateEmail,
    isPending: isUpdatingEmail,
    isError: isEmailUpdateError,
    error: emailUpdateError,
    reset: resetEmailMutation,
  } = useUpdateContactInfo();

  const {
    updateContactInfo: updatePhone,
    isPending: isUpdatingPhone,
    isError: isPhoneUpdateError,
    error: phoneUpdateError,
    reset: resetPhoneMutation,
  } = useUpdateContactInfo();

  const refreshAdvisor = useCallback(() => {
    if (advisor?.advisorId) {
      searchAdvisor(advisor.advisorId);
    }
  }, [advisor?.advisorId, searchAdvisor]);

  useEffect(() => {
    if (initialQueryRef.current) {
      searchAdvisor(initialQueryRef.current);
    }
  }, [searchAdvisor]);

  useEffect(() => {
    if (advisor) {
      emailForm.reset({ advisorEmail: advisor.advisorEmail ?? '' });
      phoneForm.reset({ advisorPhoneNumber: advisor.advisorPhoneNumber ?? '' });
      resetEmailMutation();
      resetPhoneMutation();
    }
  }, [advisor, emailForm, phoneForm, resetEmailMutation, resetPhoneMutation]);

  const onSearchAdvisor = (data: z.infer<typeof searchSchema>) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set('advisorId', data.advisorQuery);
        return next;
      },
      { replace: true },
    );
    searchAdvisor(data.advisorQuery);
  };

  const onUpdateEmail = async (data: z.infer<typeof emailSchema>) => {
    if (!advisor) return;
    if (data.advisorEmail === advisor.advisorEmail) return;

    await updateEmail({
      advisorId: advisor.advisorId,
      advisorFieldKey: 'advisorEmail',
      advisorFieldValue: data.advisorEmail,
    }).then(refreshAdvisor);
  };

  const onUpdatePhone = async (data: z.infer<typeof phoneSchema>) => {
    if (!advisor) return;
    const normalized = normalizePhoneNumber(data.advisorPhoneNumber);
    if (normalized === advisor.advisorPhoneNumber) return;

    await updatePhone({
      advisorId: advisor.advisorId,
      advisorFieldKey: 'advisorPhoneNumber',
      advisorFieldValue: normalized,
    }).then(refreshAdvisor);
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={searchForm.handleSubmit(onSearchAdvisor)}
        className="space-y-4"
      >
        <FormInput
          label="Advisor ID or Email"
          id="advisorQuery"
          type="text"
          placeholder="Enter advisor ID or email address"
          error={searchForm.formState.errors.advisorQuery}
          register={searchForm.register('advisorQuery')}
          required={true}
        />

        <PrimarySecondaryButtonContainer>
          <SubmitButton
            label="Search Advisor"
            isLoading={isSearching}
          />
        </PrimarySecondaryButtonContainer>

        {isSearchError && (
          <FormErrorAlert
            mainErrorMessage={getErrorMessage(searchError, 'Advisor not found')}
            className="mt-4"
          />
        )}
      </form>

      {advisor && (
        <Card className="space-y-4 p-6">
          <div className="border-b pb-4">
            <h3 className="text-lg font-medium">Advisor Information</h3>
            <div className="mt-2 text-sm text-gray-700">
              <p>
                <strong>ID:</strong> {advisor.advisorId}
              </p>
              <p>
                <strong>Name:</strong> {advisor.advisorFirstName} {advisor.advisorLastName}
              </p>
              <p>
                <strong>Email:</strong> {advisor.advisorEmail ?? 'N/A'}
              </p>
              <p>
                <strong>Phone:</strong> {advisor.advisorPhoneNumber ?? 'N/A'}
              </p>
              <p>
                <strong>Auth0 Type:</strong> {advisor.auth0UserId?.split('|')[0] ?? 'N/A'}
              </p>
            </div>
          </div>

          {!UPDATABLE_AUTH_TYPES.has(advisor.auth0UserId?.split('|')[0] ?? '') ? (
            <FormErrorAlert
              mainErrorMessage={getAuthTypeErrorMessage(advisor.auth0UserId)}
              className="mt-2"
            />
          ) : (
            <>
              <form
                onSubmit={emailForm.handleSubmit(onUpdateEmail)}
                className="space-y-4 border-b pb-4"
              >
                <h4 className="text-base font-medium">Update Email</h4>
                <FormInput
                  label="Email"
                  id="advisorEmail"
                  type="email"
                  placeholder="advisor@example.com"
                  error={emailForm.formState.errors.advisorEmail}
                  register={emailForm.register('advisorEmail')}
                  required={true}
                />
                <PrimarySecondaryButtonContainer>
                  <SubmitButton
                    label="Save"
                    isLoading={isUpdatingEmail}
                  />
                </PrimarySecondaryButtonContainer>
                {isEmailUpdateError && (
                  <FormErrorAlert
                    mainErrorMessage={getErrorMessage(emailUpdateError, 'Failed to update email')}
                    className="mt-2"
                  />
                )}
              </form>

              <form
                onSubmit={phoneForm.handleSubmit(onUpdatePhone)}
                className="space-y-4"
              >
                <h4 className="text-base font-medium">Update Phone Number</h4>
                <FormInput
                  label="Phone Number"
                  id="advisorPhoneNumber"
                  type="tel"
                  placeholder="(555) 555-5555"
                  error={phoneForm.formState.errors.advisorPhoneNumber}
                  register={phoneForm.register('advisorPhoneNumber')}
                  required={true}
                />
                <PrimarySecondaryButtonContainer>
                  <SubmitButton
                    label="Save"
                    isLoading={isUpdatingPhone}
                  />
                </PrimarySecondaryButtonContainer>
                {isPhoneUpdateError && (
                  <FormErrorAlert
                    mainErrorMessage={getErrorMessage(
                      phoneUpdateError,
                      'Failed to update phone number',
                    )}
                    className="mt-2"
                  />
                )}
              </form>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
